import React from "react";
import { formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { ScanBarcode, PackagePlus, Percent, X } from "lucide-react";
import { ItemRow, orderLineFinalTotal } from "./ItemRow";
// IMMUTABLE billing brain — every surcharge / tax / price-mode rate,
// every per-line decomposition, and every rate label routes through
// BillingEngine.jsx. Do NOT inline surcharge/tax math here — see
// SYSTEM_RULES.md · "Billing Engine Isolation".
import { extractRatesFromBreakdown, summarizeBreakdown } from "@/lib/BillingEngine";

/* ---------------- OrderItemsCard ----------------
 *  Renders the full "Order Items" card that lives on the OrderDetail
 *  page: the rose header strip (Add Items / Apply Special / SKU scan),
 *  the item Table (delegated to ItemRow — one row per line), and the
 *  invoice Summary block (Base, per-tax surcharge/PPN, gift-card note,
 *  price-mode hint, Total, Amount Paid, Balance Due).
 *
 *  Kept as a "dumb" card that receives everything from OrderDetail as
 *  props — every mutating action (adjustItem, setItemStaff,
 *  setItemQuantity, removeItem, applyInvoiceSpecial, removeInvoiceSpecial,
 *  clearLoyaltyOnOrder, setLineRefundIdx, editingItemIdx, scan, doScan)
 *  still lives on the parent so a single `refresh()` after each server
 *  round-trip continues to drive re-renders across the whole detail
 *  view (header card, payment tabs, receipt buttons, etc.).
 * ------------------------------------------------------------------ */
export default function OrderItemsCard({
  order, branch, staffList, specials,
  isClosed, editingItemIdx, setEditingItemIdx,
  scan, setScan, doScan,
  onAddItems, onApplySpecial, onRemoveInvoiceSpecial,
  adjustItem, setItemStaff, setItemQuantity, removeItem,
  clearLoyaltyOnOrder, setLineRefundIdx,
  canRefundLineItem,
  businessDateLoading, isSameBusinessDay,
  canAuthoriseFinance, canEditPos,
  requestPriceOverride,
  total, paid, due,
}) {
  return (
    <Card className="rounded-lg text-xs" data-testid="order-items-card-compact">
      <CardContent className="p-0">
        <div className="px-3 py-1.5 bg-rose-50 border-b border-rose-100 flex items-center gap-4 flex-wrap">
          <span className="uppercase tracking-wider text-[10px] font-semibold text-rose-900">Order Items</span>
          <Button variant="ghost" size="sm" onClick={onAddItems} className="gap-1 h-6 px-2 text-[11px] text-rose-800 hover:text-rose-900 hover:bg-rose-100/50" disabled={isClosed} data-testid="add-items-btn">
            <PackagePlus className="w-3 h-3" /> Add Items
          </Button>
          <Button variant="ghost" size="sm" onClick={onApplySpecial} className="gap-1 h-6 px-2 text-[11px] text-rose-800 hover:text-rose-900 hover:bg-rose-100/50" disabled={isClosed} data-testid="apply-special-btn">
            <Percent className="w-3 h-3" /> Apply Special
          </Button>
          <div className="flex items-center gap-1.5 ml-auto">
            <ScanBarcode className="w-3 h-3 text-rose-900" />
            <span className="text-[10px] text-rose-900">SCAN</span>
            <Input
              className="h-6 w-52 bg-white text-xs"
              placeholder="type SKU + Enter"
              value={scan}
              onChange={(e) => setScan(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doScan()}
              disabled={isClosed}
              data-testid="scan-input"
            />
          </div>
        </div>

        <Table className="[&_th]:h-7 [&_th]:py-1 [&_td]:py-1 [&_tr]:h-auto text-xs">
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead className="text-[11px]">Item</TableHead>
              <TableHead className="text-[11px]">Employee / Commission To</TableHead>
              <TableHead className="w-14 text-center text-[11px]">Qty</TableHead>
              <TableHead className="text-right text-[11px]">List Price</TableHead>
              <TableHead className="text-[11px]">Adjustment</TableHead>
              <TableHead className="text-right text-[11px]">Final Price</TableHead>
              <TableHead className="w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(order.items || []).length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">Cart is empty. Click <span className="font-medium">Add Items</span>.</TableCell></TableRow>
            ) : (() => {
              // Per-line loyalty share + rate summaries so each row can
              // (a) show its slice of the invoice-wide points discount in
              // the Adjustment column, (b) strike through the pre-loyalty
              // total in the Final Price column, and (c) render Base /
              // Service Charge / PPN sub-lines beneath the row.
              const br = order.breakdown || {};
              const totalLoyalty = Number(br.loyalty_discount || 0);
              // Rates come from BillingEngine — the persisted breakdown
              // is the authoritative snapshot for THIS invoice. Zero
              // hardcoded percentages here or anywhere downstream.
              const { priceMode, surchargeRate: sRate, taxRate: tRate } = extractRatesFromBreakdown(br, branch);
              // Split the loyalty discount proportionally across every
              // non-gift-card, non-commission-only line (mirrors the
              // backend rule that gift cards don't consume points).
              const eligibleTotals = (order.items || []).map((it) => {
                if (it.kind === "gift_card" || it.commission_only) return 0;
                return orderLineFinalTotal(it);
              });
              const eligibleSum = eligibleTotals.reduce((a, b) => a + b, 0);
              return (order.items || []).map((it, i) => (
                <ItemRow
                  key={i}
                  index={i}
                  item={it}
                  orderId={order.id}
                  branch={branch}
                  staffList={staffList}
                  specials={specials}
                  editing={editingItemIdx === i}
                  disabled={isClosed}
                  onOpen={() => setEditingItemIdx(editingItemIdx === i ? null : i)}
                  onAdjust={(adj) => adjustItem(i, adj)}
                  onSetStaff={(ids) => setItemStaff(i, ids)}
                  onSetQuantity={(q) => setItemQuantity(i, q)}
                  onRemove={() => removeItem(i)}
                  onClearLoyalty={clearLoyaltyOnOrder}
                  onRefundLineItem={(idx) => setLineRefundIdx(idx)}
                  onRequestPriceOverride={requestPriceOverride}
                  canFinalizePrice={canAuthoriseFinance}
                  canRefundLineItem={
                    canRefundLineItem !== undefined
                      ? canRefundLineItem(it)
                      : (
                          order.status === "completed"
                          && !businessDateLoading
                          && !isSameBusinessDay
                          && !it.refunded
                          // One-time-only refund lock — once a line has
                          // been refunded (full or partial) the row is
                          // permanently sealed. `refund_locked` is the
                          // authoritative flag; older orders without it
                          // fall back to `refunded_at` / any prior
                          // `refunded_credit` on the row.
                          && !it.refund_locked
                          && !it.refunded_at
                          && !(Number(it.refunded_credit || 0) > 0)
                          && (
                            // Regular non-commission row → always eligible.
                            !it.commission_only
                            // Package member sub-row → still eligible (refund
                            // that specific service at its discounted rate).
                            || ((it.adjustment && (it.adjustment.name || "").toLowerCase() === "package discount"))
                          )
                          && (canAuthoriseFinance || canEditPos)
                        )
                  }
                  loyaltyShare={eligibleSum > 0 ? (totalLoyalty * (eligibleTotals[i] / eligibleSum)) : 0}
                  loyaltyPoints={Number(br.loyalty_points_used || 0)}
                  priceMode={priceMode}
                  surchargeRate={sRate}
                  taxRate={tRate}
                  orderOpen={order.status === "open"}
                />
              ));
            })()}
          </TableBody>
        </Table>

        <div className="px-4 py-2 bg-muted/30 flex justify-end" data-testid="order-summary-financials">
          <div className="w-full max-w-sm space-y-0 leading-tight text-xs">
            {order.special ? (
              <div className="flex items-center justify-between text-primary">
                <span className="flex items-center gap-1">Special: {order.special.name}
                  <Button size="icon" variant="ghost" className="h-4 w-4" onClick={onRemoveInvoiceSpecial} disabled={isClosed} data-testid="remove-invoice-special"><X className="w-2.5 h-2.5" /></Button>
                </span>
                <span>-{formatBranchMoney(branch, order.breakdown?.invoice_discount || 0)}</span>
              </div>
            ) : null}
            <div className="flex justify-between"><span>Base price:</span><span data-testid="subtotal">{formatBranchMoney(branch, order.breakdown?.base ?? order.breakdown?.subtotal ?? order.subtotal ?? 0)}</span></div>
            {/* Summary rows emitted by BillingEngine — one row per
                active surcharge + one per tax, in the exact order the
                branch config stored them. No hardcoded rate labels. */}
            {summarizeBreakdown(order.breakdown).map((row) => (
              <div key={`${row.kind}-${row.id}`} className="flex justify-between text-muted-foreground"><span>{row.name} ({row.rate}%):</span><span>{formatBranchMoney(branch, row.amount)}</span></div>
            ))}
            {(order.breakdown?.gift_subtotal || 0) > 0 && (
              <div className="text-[10px] text-muted-foreground text-right" data-testid="gift-notax-note">
                Gift cards excluded from service charge + PPN.
              </div>
            )}
            {order.breakdown?.price_mode === "nett" && (
              <div className="text-[10px] text-muted-foreground text-right" data-testid="nett-included-note">breakdown included in price</div>
            )}
            <div className="flex justify-between font-semibold border-t border-border pt-0.5 mt-0.5"><span>Total:</span><span>{formatBranchMoney(branch, total)}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>Amount Paid:</span><span>{formatBranchMoney(branch, paid)}</span></div>
            <div className="flex justify-between font-semibold"><span>Balance Due:</span><span>{formatBranchMoney(branch, due)}</span></div>
            {paid - total > 0.009 ? (
              <div className="flex justify-between font-semibold text-rose-600" data-testid="items-credit-due">
                <span>Credit Due:</span><span>{formatBranchMoney(branch, paid - total)}</span>
              </div>
            ) : null}
          </div>
        </div>

        {/* Nett-mode: breakdown now lives in the Subtotal area above. */}
      </CardContent>
    </Card>
  );
}
